const BASE = '/spring/hotel/api';

export function redirectToLogin() {
  window.dispatchEvent(new CustomEvent('hotel-session-expired'));
}

export function goToLoginPage() {
  const returnTo = encodeURIComponent(window.location.href.split('?')[0]);
  window.location.href = '/spring/login?returnTo=' + returnTo;
}

export async function hotelLogout() {
  try { await fetch('/spring/hotel/api/logout', { method: 'POST' }); } catch (_) {}
  goToLoginPage();
}

async function req(path, options = {}) {
  const res = await fetch(BASE + path, {
    headers: { 'Content-Type': 'application/json', ...options.headers },
    credentials: 'include',
    ...options,
  });
  if (res.redirected && res.url.includes('login')) { redirectToLogin(); return null; }
  if (res.status === 401 || res.status === 403) { redirectToLogin(); return null; }
  if (!res.ok) {
    const ct = res.headers.get('content-type') || '';
    if (ct.includes('text/html')) { redirectToLogin(); return null; }
    throw new Error(`${res.status} ${res.statusText}`);
  }
  if (res.status === 204) return null;
  const data = await res.json();
  if (data && data.error === 'Not authenticated') { redirectToLogin(); return null; }
  return data;
}

export const checkHotelSession = () => req('/session');

export const hotelApi = {
  // Session
  getSession: () => req('/session'),

  // Hotel Info
  getInfo: () => req('/info'),
  upsertInfo: (d) => req('/info', { method: 'POST', body: JSON.stringify(d) }),

  // Tables
  getTables: () => req('/tables'),
  createTable: (d) => req('/tables', { method: 'POST', body: JSON.stringify(d) }),
  updateTable: (id, d) => req(`/tables/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteTable: (id) => req(`/tables/${id}`, { method: 'DELETE' }),

  // Rooms
  getRooms: () => req('/rooms'),
  createRoom: (d) => req('/rooms', { method: 'POST', body: JSON.stringify(d) }),
  updateRoom: (id, d) => req(`/rooms/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteRoom: (id) => req(`/rooms/${id}`, { method: 'DELETE' }),

  // Menu Categories
  getCategories: () => req('/menu/categories'),
  createCategory: (d) => req('/menu/categories', { method: 'POST', body: JSON.stringify(d) }),
  updateCategory: (id, d) => req(`/menu/categories/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteCategory: (id) => req(`/menu/categories/${id}`, { method: 'DELETE' }),

  // Menu Items
  getMenuItems: () => req('/menu/items'),
  createMenuItem: (d) => req('/menu/items', { method: 'POST', body: JSON.stringify(d) }),
  updateMenuItem: (id, d) => req(`/menu/items/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteMenuItem: (id) => req(`/menu/items/${id}`, { method: 'DELETE' }),
  toggleMenuItem: (id) => req(`/menu/items/${id}/toggle`, { method: 'PUT', body: JSON.stringify({}) }),

  // Orders
  getOrders: () => req('/orders'),
  getOrder: (id) => req(`/orders/${id}`),
  createOrder: (d) => req('/orders', { method: 'POST', body: JSON.stringify(d) }),
  addOrderItems: (id, d) => req(`/orders/${id}/items`, { method: 'POST', body: JSON.stringify(d) }),
  updateOrderItemStatus: (orderId, itemId, d) => req(`/orders/${orderId}/items/${itemId}`, { method: 'PUT', body: JSON.stringify(d) }),
  updateOrderStatus: (id, d) => req(`/orders/${id}/status`, { method: 'PUT', body: JSON.stringify(d) }),

  // Kitchen
  getKitchen: () => req('/kitchen'),
  updateKitchenItem: (id, d) => req(`/kitchen/items/${id}`, { method: 'PUT', body: JSON.stringify(d) }),

  // Billing
  getOpenBilling: () => req('/billing/open'),
  getBillingHistory: () => req('/billing/history'),
  getBilling: (orderId) => req(`/billing/${orderId}`),
  generateBill: (orderId, d) => req(`/billing/${orderId}/generate`, { method: 'POST', body: JSON.stringify(d || {}) }),
  payBill: (orderId, d) => req(`/billing/${orderId}/pay`, { method: 'POST', body: JSON.stringify(d) }),

  // Table Bookings
  getTableBookings: (date) => req('/bookings/tables' + (date ? `?date=${date}` : '')),
  createTableBooking: (d) => req('/bookings/tables', { method: 'POST', body: JSON.stringify(d) }),
  updateTableBooking: (id, d) => req(`/bookings/tables/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteTableBooking: (id) => req(`/bookings/tables/${id}`, { method: 'DELETE' }),

  // Room Bookings
  getRoomBookings: (month) => req('/bookings/rooms' + (month ? `?month=${month}` : '')),
  createRoomBooking: (d) => req('/bookings/rooms', { method: 'POST', body: JSON.stringify(d) }),
  updateRoomBooking: (id, d) => req(`/bookings/rooms/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteRoomBooking: (id) => req(`/bookings/rooms/${id}`, { method: 'DELETE' }),

  // Inventory
  getInventory: () => req('/inventory'),
  createMaterial: (d) => req('/inventory', { method: 'POST', body: JSON.stringify(d) }),
  updateMaterial: (id, d) => req(`/inventory/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteMaterial: (id) => req(`/inventory/${id}`, { method: 'DELETE' }),

  // Purchases
  getPurchases: () => req('/purchases'),
  createPurchase: (d) => req('/purchases', { method: 'POST', body: JSON.stringify(d) }),
  deletePurchase: (id) => req(`/purchases/${id}`, { method: 'DELETE' }),

  // Kitchen Issues
  getKitchenIssues: () => req('/kitchen-issues'),
  createKitchenIssue: (d) => req('/kitchen-issues', { method: 'POST', body: JSON.stringify(d) }),
  deleteKitchenIssue: (id) => req(`/kitchen-issues/${id}`, { method: 'DELETE' }),

  // Staff
  getStaff: () => req('/staff'),
  createStaff: (d) => req('/staff', { method: 'POST', body: JSON.stringify(d) }),
  updateStaff: (id, d) => req(`/staff/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteStaff: (id) => req(`/staff/${id}`, { method: 'DELETE' }),

  // Dashboard
  getDashboard: () => req('/dashboard'),

  // Public menu (no auth)
  getPublicMenu: (ownersid, tableNo) => {
    const qs = tableNo ? `?table=${encodeURIComponent(tableNo)}` : '';
    return fetch(`/spring/hotel/api/public/${ownersid}/menu${qs}`, { cache: 'no-store' }).then(r => r.json());
  },
  createPublicOrder: (ownersid, d) => fetch(`/spring/hotel/api/public/${ownersid}/order`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(d),
  }).then(r => r.json()),
};
